import * as LucideIcons from 'lucide-react';

/**
 * Catálogo Lucide para menus / categorias / atalhos.
 * Valor persistido: `lucide:kebab-case` (Iconify + Paper).
 */

export const LUCIDE_ICON_GROUPS = [
    {
        id: 'nav',
        label: 'Navegação',
        keys: [
            'Home',
            'House',
            'LayoutDashboard',
            'Menu',
            'Search',
            'Compass',
            'Navigation',
            'MapPin',
            'Map',
            'Globe',
            'Link',
            'ExternalLink',
            'LayoutGrid',
            'Grid2x2',
            'List',
            'ListChecks',
            'Layers',
        ],
    },
    {
        id: 'people',
        label: 'Pessoas',
        keys: ['Users', 'User', 'UserRound', 'IdCard', 'HeartHandshake', 'Baby', 'Contact'],
    },
    {
        id: 'commerce',
        label: 'Comércio',
        keys: [
            'Store',
            'ShoppingBag',
            'ShoppingCart',
            'Tag',
            'Tags',
            'Ticket',
            'TicketPercent',
            'BadgePercent',
            'Percent',
            'Gift',
            'Banknote',
            'HandCoins',
            'Wallet',
            'CreditCard',
            'Receipt',
            'Package',
            'PackageCheck',
            'Boxes',
        ],
    },
    {
        id: 'health',
        label: 'Saúde',
        keys: [
            'Heart',
            'HeartPulse',
            'Stethoscope',
            'Activity',
            'Pill',
            'Syringe',
            'Hospital',
            'Brain',
            'PawPrint',
            'Dog',
            'Cat',
            'Shield',
            'ShieldCheck',
            'Umbrella',
        ],
    },
    {
        id: 'life',
        label: 'Dia a dia',
        keys: [
            'Phone',
            'PhoneCall',
            'Mail',
            'MessageCircle',
            'MessagesSquare',
            'Bell',
            'Calendar',
            'CalendarDays',
            'Clock',
            'Car',
            'Bus',
            'Plane',
            'Hotel',
            'Utensils',
            'Coffee',
            'Dumbbell',
            'Bike',
            'Briefcase',
            'Building2',
            'Landmark',
            'GraduationCap',
            'BookOpen',
            'FileText',
            'Files',
            'ClipboardList',
        ],
    },
    {
        id: 'media',
        label: 'Mídia',
        keys: [
            'Star',
            'Sparkles',
            'Zap',
            'Flame',
            'Sun',
            'Moon',
            'Camera',
            'Image',
            'Video',
            'Music',
            'Headphones',
            'Gamepad2',
            'QrCode',
            'ScanLine',
            'Leaf',
            'TreePine',
        ],
    },
    {
        id: 'system',
        label: 'Sistema',
        keys: [
            'Settings',
            'Wrench',
            'HelpCircle',
            'CircleHelp',
            'Info',
            'CheckCircle2',
            'AlertTriangle',
        ],
    },
];

function pascalToKebab(name) {
    return String(name)
        .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
        .replace(/([A-Z])([A-Z][a-z])/g, '$1-$2')
        .toLowerCase();
}

function buildEntry(key) {
    const Component = LucideIcons[key];
    if (!Component) return null;
    const slug = pascalToKebab(key);
    return {
        key,
        slug,
        value: `lucide:${slug}`,
        label: key,
        Component,
    };
}

const seen = new Set();
export const LUCIDE_ICON_CATALOG = LUCIDE_ICON_GROUPS.flatMap(group =>
    group.keys
        .map(key => {
            if (seen.has(key)) return null;
            seen.add(key);
            const entry = buildEntry(key);
            return entry ? { ...entry, groupId: group.id, groupLabel: group.label } : null;
        })
        .filter(Boolean),
);

export function normalizeStoredIcon(raw) {
    if (!raw || typeof raw !== 'string') return '';
    const t = raw.trim();
    if (!t || t.toLowerCase() === 'null') return '';
    return t;
}

/** Resolve item do catálogo a partir do valor gravado (`lucide:home`, legado, etc.). */
export function findLucideMenuIcon(raw) {
    const value = normalizeStoredIcon(raw);
    if (!value) return null;

    if (value.startsWith('lucide:')) {
        const slug = value.slice('lucide:'.length);
        return LUCIDE_ICON_CATALOG.find(i => i.slug === slug || i.value === value) || null;
    }

    if (value.includes(':')) return null;

    const slug = value
        .replace(/^mdi/i, '')
        .replace(/([A-Z])/g, m => `-${m.toLowerCase()}`)
        .replace(/^-/, '');
    return LUCIDE_ICON_CATALOG.find(i => i.slug === slug || i.slug === value) || null;
}

export function filterLucideIcons(catalog, search, groupId) {
    let list = catalog;
    if (groupId && groupId !== 'all') {
        list = list.filter(i => i.groupId === groupId);
    }
    const q = String(search || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim();
    if (!q) return list;
    return list.filter(
        i =>
            i.slug.includes(q) ||
            i.label.toLowerCase().includes(q) ||
            i.groupLabel.toLowerCase().includes(q) ||
            i.value.includes(q),
    );
}
